import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import type { BlogPost } from '../../shared/models/blog.model';
import { CrcPipe } from '../../shared/pipes/crc.pipe';
import { BLOG_COPY } from './constants/blog.constants';
import type { BlogArticleResolved } from './data/blog-article.resolver';
import { isBlogLoadError, type BlogArticleView } from './data/blog-view.model';
import { toBlogProductView, toStoreCategoryViews } from './utils/blog-links.util';

import { ReadingProgressBarComponent } from './components/reading-progress-bar/reading-progress-bar.component';
import { ArticleHeaderComponent } from './components/article-header/article-header.component';
import { ArticleTocComponent } from './components/article-toc/article-toc.component';
import { ArticleBodyComponent } from './components/article-body/article-body.component';
import { ArticleSidebarComponent } from './components/article-sidebar/article-sidebar.component';
import { ArticleAuthorCardComponent } from './components/article-author-card/article-author-card.component';
import { ArticleFooterComponent } from './components/article-footer/article-footer.component';
import { ArticleCommentsComponent } from './components/article-comments/article-comments.component';
import { BlogCardComponent } from './components/blog-card/blog-card.component';

@Component({
  selector: 'app-blog-article-page',
  imports: [
    RouterLink,
    CrcPipe,
    ReadingProgressBarComponent,
    ArticleHeaderComponent,
    ArticleTocComponent,
    ArticleBodyComponent,
    ArticleSidebarComponent,
    ArticleAuthorCardComponent,
    ArticleFooterComponent,
    ArticleCommentsComponent,
    BlogCardComponent,
  ],
  templateUrl: './blog-article-page.component.html',
  styleUrl: './blog-article-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
    '[style.--article-accent-color]': 'post()?.game?.accentColor',
  },
})
export class BlogArticlePageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  protected readonly copy = BLOG_COPY;

  /** Vista resuelta por `blogArticleResolver`; `null` = slug inexistente (B62). */
  private readonly article = signal<BlogArticleView | null>(null);

  /** La API no respondió (B123): estado de error en lugar de «no encontrado». */
  protected readonly loadError = signal(false);

  protected readonly post = computed<BlogPost | null>(() => this.article()?.post ?? null);
  protected readonly activeSection = signal('');

  protected readonly prevPost = computed(() => this.article()?.prev ?? null);
  protected readonly nextPost = computed(() => this.article()?.next ?? null);
  protected readonly relatedPosts = computed(() => this.article()?.related ?? []);

  protected readonly hasContent = computed(() => !!this.post()?.content);

  /** Bloque móvil: mismas reglas que la barra lateral (B68, B71). */
  protected readonly relatedProducts = computed(() =>
    (this.post()?.relatedProducts ?? []).map(toBlogProductView),
  );
  protected readonly storeCategories = computed(() =>
    toStoreCategoryViews(this.post()?.relatedStoreCategories),
  );

  constructor() {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      const resolved = data['article'] as BlogArticleResolved | undefined;
      this.loadError.set(isBlogLoadError(resolved));
      this.article.set(resolved && !isBlogLoadError(resolved) ? resolved : null);
      this.activeSection.set('');
    });

    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      this.setupScrollSpy();
    });
  }

  /** Reintento del estado de error: recarga, y el SSR vuelve a pedir (B123). */
  protected retry(): void {
    this.document.location.reload();
  }

  private setupScrollSpy(): void {
    const post = this.post();
    if (!post?.content) return;

    const sectionIds = post.content.sections.map((s) => s.id);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.activeSection.set(entry.target.id);
          }
        }
      },
      {
        rootMargin: '-120px 0px -60% 0px',
        threshold: 0,
      },
    );

    for (const id of sectionIds) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }

    this.destroyRef.onDestroy(() => observer.disconnect());
  }
}
